import React, { FC, FormEvent } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'src/components/widgets/badge';
import { backofficeUrl, stringFormat, T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { usePrintTypeInfo } from 'src/shared/hooks/usePrintTypeInfo';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled, { css } from 'styled-components';
import { Link, PrimaryButton } from '../../widgets/buttons';
import { MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { showErrorNotification } from '../../widgets/notificationToaster';
import { SwitchGroup } from '../../widgets/switchGroup';
import { ZTooltip } from '../../widgets/ztooltip';
import { ECommerce, PlanBillingPeriod, PricingVersion } from './__generated__/choosePlanQuery.graphql';
import { sharedRemoveAddOnMutation, sharedRemoveAddOnMutation$data } from './__generated__/sharedRemoveAddOnMutation.graphql';
import { AdditionalPlanFeatures, IPlan, Subscription } from './helpers';

// Tooltip controls
export const TooltipContent = styled.div`
    font-family: 'Inter', 'Nunito', san-serif;
    font-size: 14px;
    text-align: left;
    line-height: 18px;

    a {
        color: ${(props) => props.theme.app.secondary};
        font-weight: bold;
    }
`;

export const TooltipV2 = styled(ZTooltip)`
    height: 20px;
    margin-left: 3px;
    svg {
        cursor: pointer;
        font-size: 20px;
        color: ${(props) => props.theme.app.secondary};
    }
`;

export const LinkInline = styled(Link)`
    margin-left: 3px;
`;

export const LinkBreakRow = styled(Link)`
    display: block;
    width: 100%;
`;

// Switcher
const SwitchContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    > label {
        color: ${(props) => props.theme.app.component.card.text.color};
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        &.checked {
            font-weight: 700;
        }
        > label.discount {
            font-weight: 700;
            color: ${(props) => props.theme.app.secondary};
            margin-left: 3px;
        }
        &:last-child {
            margin-left: 10px;
            margin-right: 0;
        }
    }

    @media screen and (max-width: 1024px) {
        margin-bottom: 20px;
        > label {
            font-size: 16px;
            line-height: 20px;
        }
    }
`;

const BillingPeriodSwitchGroup = styled(SwitchGroup)`
    transform: rotate(180deg);
`;

export const PricingPeriodSwitcher: FC<{
    discount: number;
    onChangePeriod: (period: PlanBillingPeriod) => void;
    period: PlanBillingPeriod;
}> = (props) => {
    const onSwitch = (checked: boolean) => {
        checked ? props.onChangePeriod('ANNUALLY') : props.onChangePeriod('MONTHLY');
    };

    return (
        <SwitchContainer>
            <label className={props.period === 'ANNUALLY' ? 'checked' : ''}>
                Annually <label className='discount'>{`${T._('Save', 'Admin')} ${props.discount}%`}</label>
            </label>
            <BillingPeriodSwitchGroup checked={props.period === 'ANNUALLY'} onChange={onSwitch} label={''} />
            <label className={props.period === 'MONTHLY' ? 'checked' : ''}>Monthly</label>
        </SwitchContainer>
    );
};

export const buttonAnimation = css`
    transition: transform 0.3s ease-out;
    &:hover:not([disabled]) {
        transform: translateY(-8px);
    }
`;

export const BadgePlan = styled.div`
    display: flex;
    justify-content: center;
    position: absolute;
    width: 100%;
    left: 0;
    top: -15px;
    div {
        border: 1px solid #f46200;
        border-radius: 9999px;
        font-size: 14px;
        font-weight: 700;
        padding: 0 12px;
    }
`;

// Viewer Plan
const ViewerPlanContainer = styled.div`
    position: relative;
    font-family: 'Inter', 'Nunito', san-serif;
    display: flex;
    flex-direction: row;
    margin: 20px 0 20px;
    background: #fff;
    border: 1px solid #c7c7c7;
    box-sizing: border-box;
    border-radius: 8px;
    padding: 24px;
`;

const ViewerDescriptionColumn = styled.div`
    width: calc(100% - 288px);

    > div {
        width: auto;
        left: 20px;
    }

    > h2 {
        color: #263238;
        font-size: 24px;
        font-weight: bold;
        margin: 10px 0 20px;
    }
    > p {
        padding: 0;
        margin: 0;
        > a {
            color: #ff6600;
        }
    }
`;
const ViewerPriceColumn = styled.div`
    width: 288px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;
const ViewerPeriod = styled.p``;
const ViewerPrice = styled.label`
    font-weight: bold;
    font-size: 16px;
    color: #263238;
    margin-top: 5px;
    text-align: right;
`;
const TooltipAlignContentRight = styled(ZTooltip)`
    margin: 0 0 0 auto;
`;

export const ViewerPlan: FC<{
    planData: IPlan;
    subscription: Subscription;
    ecommerce: ECommerce;
    currencySymbol: string;
}> = (props) => {
    return (
        <ViewerPlanContainer>
            <ViewerDescriptionColumn>
                <BadgePlan>
                    <Badge badgeColor={'#FFF'} color={'#ff6600'} showCircle={false}>
                        New product
                    </Badge>
                </BadgePlan>
                <h2>{'3D/AR Viewer'}</h2>
                <p>
                    {T._('Showcase your products without hassle with our innovative tool!', 'Admin')}{' '}
                    <Link target='_blank' data-track='learn more viewer' href='https://zakeke.zendesk.com/hc/en-us/articles/4408151478162-3D-AR-Viewer'>
                        {T._('Learn more.', 'Admin')}
                    </Link>
                </p>
            </ViewerDescriptionColumn>
            <ViewerPriceColumn>
                <ViewerPrice>
                    {props.currencySymbol} {props.planData?.price.toFixed(2)}/month
                </ViewerPrice>
                <ViewerPeriod></ViewerPeriod>
                <ButtonSubscribePlan
                    planID={props.planData.planID}
                    profileName='Viewer'
                    salesChannelPrice={props.planData.salesChannelPrice}
                    ecommerce={props.ecommerce}
                    planPeriodSelected='MONTHLY'
                    subscription={props.subscription}
                    isPricingForCustomPlan={false}
                    freeTrialDays={props.planData.trialPeriod}
                    planName={props.planData.name}
                    planVersion={props.planData.version}
                    startFreeTrialLabel={T._('Try our 3D/AR viewer', 'Admin')}
                />
            </ViewerPriceColumn>
        </ViewerPlanContainer>
    );
};

// Button Subscribe plan
const FormPlanSubscribe = styled.form<{ $version?: PricingVersion }>`
    display: flex;
    align-items: center;
    justify-content: center;
    button {
        margin: 0 auto;
        width: ${(p) => (p.$version === 'VERSION_2' ? '150px' : '100%')};
    }
`;

// Button subscribe
const _buttonSubscribePlan: FC<{
    planName: string;
    additionalFeaturesPlan?: string | null;
    hasPlanAdvancedPricing?: boolean | null;
    hasPantoneEnabled?: boolean | null;
    startFreeTrialLabel?: string;
    planID: number;
    planPeriodSelected: PlanBillingPeriod;
    profileName?: string;
    ecommerce: ECommerce;
    freeTrialDays: number;
    subscription: Subscription;
    isPricingForCustomPlan: boolean;
    salesChannelPrice: number;
    numSalesChannelActive?: number;
    numProfanityChecksMade?: number;
    moqRulesCount?: number;
    additionalAttributesCount?: number;
    planVersion: PricingVersion;
    isMarketingAccepted?: boolean;
    checkValidation?: () => boolean;
    disabled?: boolean;
    tooltip?: string | null;
    className?: string;
}> = (props) => {
    // Dialog Manager
    const { showDialog, closeDialog } = useDialogManager();
    const { isAllowed } = usePermissions();

    // Navigate hook
    const navigate = useNavigate();
    const subscriptionAddons = useSubscriptionData().subscription?.addons ?? [];
    const currentPlan = useSubscriptionData().subscription?.plan;
    const hasSellerPrintTypesWithPantoneCmykPalette = usePrintTypeInfo().hasPalettePantoneCMYK;

    const urlAction =
        props.ecommerce == 'SHOPIFY'
            ? '/subscription/SubscribeShopify'
            : props.subscription == null || props.subscription == undefined
              ? '/subscription/Seller/Subscription'
              : '/subscription/Seller/Subscription/ChangePlan';
    const startFreeTrialLabel = props.startFreeTrialLabel != undefined ? props.startFreeTrialLabel : T._('Try for free', 'Admin');
    const buttonText = props.subscription == null || props.subscription == undefined ? startFreeTrialLabel : T._('Change Plan', 'Admin');
    const additionalFeaturesForNewPlan = JSON.parse(props.additionalFeaturesPlan || '{}') as AdditionalPlanFeatures;

    //check on addons subscribed
    const quoteButtonAddon = subscriptionAddons.find((addon) => addon.name.indexOf('QUOTATION_BUTTON') > -1);
    const profanityAddon = subscriptionAddons.find((addon) => addon.name.indexOf('PROFANITY') > -1);
    const largeFileAddon = subscriptionAddons.find((addon) => addon.name.indexOf('LARGE_FILES') > -1);
    let addonIDtoCheck: number[] = [];
    let isGetAQuoteAddonIsEnabled = false;
    let isProfanityAddonIsEnabled = false;
    let isLargeFileAddonIsEnabled = false;

    if (quoteButtonAddon) {
        isGetAQuoteAddonIsEnabled = true;
        addonIDtoCheck.push(quoteButtonAddon.addonID);
    }
    if (profanityAddon) {
        isProfanityAddonIsEnabled = true;
        addonIDtoCheck.push(profanityAddon.addonID);
    }
    if (largeFileAddon) {
        isLargeFileAddonIsEnabled = true;
        addonIDtoCheck.push(largeFileAddon.addonID);
    }
    const checkGetAQuoteDowngrade = additionalFeaturesForNewPlan.getAQuote === false && isGetAQuoteAddonIsEnabled;
    const checkProfanityDowngrade = additionalFeaturesForNewPlan.profanityFilter === false && isProfanityAddonIsEnabled;
    const checkLargeFileDowngrade = additionalFeaturesForNewPlan.largeFile === false && isLargeFileAddonIsEnabled;

    const [removeAddOn] = useMutation<sharedRemoveAddOnMutation>(graphql`
        mutation sharedRemoveAddOnMutation($addonId: Long!) {
            removeAddon(addonId: $addonId)
        }
    `);

    const onSubmitForm = (e: FormEvent) => {
        e.preventDefault();

        if (props.checkValidation && !props.checkValidation()) return;

        if (!isAllowed(['SUBSCRIPTION'])) {
            showErrorNotification({
                message: T._("You don't have permission to perform this action.", 'Admin')
            });
            return;
        }

        const form = e.currentTarget as HTMLFormElement;
        if (props.subscription && props.subscription.planId == props.planID) {
            showDialog(
                'currentPlanSelected',
                <MessageDialog
                    message={T._('You have selected your current plan.', 'Admin')}
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('currentPlanSelected');
                            }
                        }
                    ]}
                />
            );
        } else if (props.subscription && props.profileName == 'Viewer' && props.planVersion == 'VERSION_1') {
            showDialog(
                'confirm',
                <QuestionDialog
                    buttonYesLabel={T._('Confirm', 'Admin')}
                    buttonNoLabel={T._('Cancel', 'Admin')}
                    showCloseButton={false}
                    message={
                        <React.Fragment>
                            <p>
                                {T._('You are changing your current plan to the 3D/AR Viewer.', 'Admin')}
                                <br />
                                <strong>{T._('You will no longer have access to the Product Customizer and/or 3D Product Configurator.', 'Admin')}</strong>
                                <br />
                                {T._('Do you wish to continue?', 'Admin')}
                            </p>
                            <p>{T._('If you only wish to test the 3D/AR Viewer, please drop us a message at help@zakeke.com', 'Admin')}</p>
                        </React.Fragment>
                    }
                    onNoClick={() => {
                        closeDialog('confirm');
                    }}
                    onYesClick={() => {
                        closeDialog('confirm');
                        form.submit();
                    }}
                />
            );
        } else if (
            props.numProfanityChecksMade != undefined &&
            props.planVersion == 'VERSION_1' &&
            props.subscription != null &&
            props.numProfanityChecksMade > 1 &&
            props.profileName == 'Starter'
        ) {
            showDialog(
                'changeToPlanWithoutProfanityChecks',
                <MessageDialog
                    message={T._('You can not change to STARTER plan cause you have profanity checks enabled.', 'Admin')}
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('changeToPlanWithoutProfanityChecks');
                            }
                        }
                    ]}
                />
            );
        } else if (props.salesChannelPrice < 0 && (props?.numSalesChannelActive ?? 0) > 1) {
            showDialog(
                'changeToPlanWithoutSalesChannels',
                <MessageDialog
                    message={T._('You can not change to STARTER plan cause you have sales channels configured.', 'Admin')}
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('changeToPlanWithoutSalesChannels');
                            }
                        }
                    ]}
                />
            );
        } else if (props.subscription != null && (props.profileName === 'Starter' || props.profileName === 'NewStarter') && (props.moqRulesCount ?? 0) > 0) {
            showDialog(
                'changeToPlanWithoutMoqRules',
                <MessageDialog
                    message={T._('You can not downgrade to the STARTER plan because you have MOQ rules configured.', 'Admin')}
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('changeToPlanWithoutMoqRules');
                            }
                        }
                    ]}
                />
            );
        } else if (
            props.subscription != null &&
            (props.profileName === 'Starter' || props.profileName === 'NewStarter') &&
            (props.additionalAttributesCount ?? 0) > 0
        ) {
            showDialog(
                'changeToPlanWithoutExtraOptions',
                <MessageDialog
                    message={T._('You can not downgrade to the STARTER plan because you have Extra Options configured.', 'Admin')}
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('changeToPlanWithoutExtraOptions');
                            }
                        }
                    ]}
                />
            );
        } else if (hasSellerPrintTypesWithPantoneCmykPalette && props.hasPantoneEnabled === false) {
            showDialog(
                'changeToPlanWithoutPantoneCMYK',
                <MessageDialog
                    message={
                        <>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: stringFormat(
                                        T._(
                                            "You can't downgrade because you're using the {0} feature, which isn't included in the plan you want to downgrade to.",
                                            'Admin'
                                        ),
                                        '<a target="_blank" href="https://zakeke.zendesk.com/hc/en-us/articles/4402564616978">Pantone/CMYK Mode</a>'
                                    )
                                }}
                            />
                            <br />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: stringFormat(
                                        T._("Please disable this feature from the {0} where it's used, and then try downgrading again.", 'Admin'),
                                        `<strong>${T._('Printing Methods', 'Admin')}</strong>`
                                    )
                                }}
                            />
                        </>
                    }
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('changeToPlanWithoutPantoneCMYK');
                            }
                        }
                    ]}
                />
            );
        } else if (
            props.planVersion == 'VERSION_2' &&
            props.profileName != props.subscription?.profileName &&
            !props.isPricingForCustomPlan &&
            (props.subscription?.profileName ?? '').toLowerCase().indexOf('starter') < 0 &&
            (checkLargeFileDowngrade || checkProfanityDowngrade || checkGetAQuoteDowngrade)
        ) {
            const messageLossFeatures = (
                <>
                    <label>Please note that downgrading your plan will result in the loss of access to the following features:</label>
                    <br />
                    <ul>
                        {checkGetAQuoteDowngrade && <li>Get a Quote tool</li>}
                        {checkProfanityDowngrade && <li>Profanity Filter Addon</li>}
                        {checkLargeFileDowngrade && <li>Large File Addon</li>}
                    </ul>
                    <label>If the features being disabled have an impact on the end-user's side.</label>
                    <br />
                    <label>Could you confirm you want to proceed to remove the addons and so to downgrade the plan now?</label>
                </>
            );
            showDialog(
                'downgradeWithFeaturesEnabled',
                <QuestionDialog
                    buttonNoLabel={T._('No, keep current plan', 'Admin')}
                    buttonYesLabel={T._('Downgrade', 'Admin')}
                    onYesClick={async () => {
                        try {
                            const result = await Promise.all(
                                addonIDtoCheck.map(async (addonID) => {
                                    return new Promise<sharedRemoveAddOnMutation$data>((resolve, reject) => {
                                        removeAddOn({
                                            onCompleted: resolve,
                                            variables: {
                                                addonId: addonID
                                            },
                                            onError: reject
                                        });
                                    });
                                })
                            );

                            form.submit();
                        } catch (e) {
                            console.error(e);
                            showErrorNotification({
                                message: T._('Something went wrong. Please try again or contact us at help@zakeke.com.', 'Admin'),
                                dismiss: { duration: 15000 }
                            });
                        }
                    }}
                    onNoClick={() => {
                        closeDialog('downgradeWithFeaturesEnabled');
                        if (document.referrer.toLowerCase().indexOf('composer') > 0) window.location.href = '/Composer';
                        else navigate(backofficeUrl('/Dashboard'));
                    }}
                    message={messageLossFeatures}
                />
            );
        } else if (
            props.planVersion == 'VERSION_2' &&
            props.profileName != props.subscription?.profileName &&
            !props.isPricingForCustomPlan &&
            (props.subscription?.profileName ?? '').toLowerCase().indexOf('starter') < 0 &&
            ((additionalFeaturesForNewPlan.bulkVariationForm === false && props.subscription?.additionalFeaturesEnabled.hasBulkVariationForms) ||
                (additionalFeaturesForNewPlan.materialLibrary === false && props.subscription?.additionalFeaturesEnabled.hasMaterialLibrary) ||
                (additionalFeaturesForNewPlan.dynamicProperties === false && props.subscription?.additionalFeaturesEnabled.hasDynamicProperties) ||
                (additionalFeaturesForNewPlan.editPDFPreview === false && props.subscription?.additionalFeaturesEnabled.hasPDFPreviewSettings) ||
                (additionalFeaturesForNewPlan.editSummary === false && props.subscription?.additionalFeaturesEnabled.hasPDFSummarySettings) ||
                (additionalFeaturesForNewPlan.prices3dConfigurator === false &&
                    props.subscription?.additionalFeaturesEnabled.hasAdvancingPricingRulesConfigurator) ||
                (additionalFeaturesForNewPlan.multiareaConfiguratorDesigner === false &&
                    props.subscription?.additionalFeaturesEnabled.hasMultiAreaDesignerConfigurator) ||
                (props.hasPlanAdvancedPricing === false && props.subscription?.additionalFeaturesEnabled.hasAdvancingPricingRulesCustomizer) ||
                (additionalFeaturesForNewPlan.vectorization === false && currentPlan?.additionalFeatures.vectorization === true))
        ) {
            const messageLossFeatures = (
                <>
                    <label>Please note that downgrading your plan will result in the loss of access to the following features:</label>
                    <br />
                    <ul>
                        {props.subscription?.additionalFeaturesEnabled.hasBulkVariationForms && additionalFeaturesForNewPlan.bulkVariationForm === false && (
                            <li>Bulk Variation Form</li>
                        )}
                        {props.subscription?.additionalFeaturesEnabled.hasPDFPreviewSettings && additionalFeaturesForNewPlan.editPDFPreview === false && (
                            <li>Custom PDF preview</li>
                        )}
                        {props.subscription?.additionalFeaturesEnabled.hasPDFSummarySettings && additionalFeaturesForNewPlan.editSummary === false && (
                            <li>Custom Order Summary</li>
                        )}
                        {props.subscription?.additionalFeaturesEnabled.hasMultiAreaDesignerConfigurator &&
                            additionalFeaturesForNewPlan.multiareaConfiguratorDesigner === false && <li>Design Multiple Areas</li>}
                        {props.subscription?.additionalFeaturesEnabled.hasDynamicProperties && additionalFeaturesForNewPlan.dynamicProperties === false && (
                            <li>Dynamic poduct properties</li>
                        )}
                        {props.subscription?.additionalFeaturesEnabled.hasAdvancingPricingRulesCustomizer && props.hasPlanAdvancedPricing === false && (
                            <li>Dynamic Pricing rules</li>
                        )}
                        {props.subscription?.additionalFeaturesEnabled.hasMaterialLibrary && additionalFeaturesForNewPlan.materialLibrary === false && (
                            <li>Material Library</li>
                        )}
                        {props.subscription?.additionalFeaturesEnabled.hasAdvancingPricingRulesConfigurator &&
                            additionalFeaturesForNewPlan.prices3dConfigurator === false && <li>Pricing rules</li>}
                        {currentPlan?.additionalFeatures.vectorization === true && additionalFeaturesForNewPlan.vectorization === false && (
                            <li>AI Vectorizer</li>
                        )}
                    </ul>
                    <label>
                        If the features being disabled have an impact on the end-user's side, please note that they will also be disabled in the Zakeke UI for
                        your customers.
                    </label>
                    <br />
                    <label>Are you sure you want to proceed with the downgrade now?</label>
                </>
            );
            showDialog(
                'downgradeWithFeaturesEnabled',
                <QuestionDialog
                    buttonNoLabel={T._('No, keep current plan', 'Admin')}
                    buttonYesLabel={T._('Downgrade', 'Admin')}
                    onYesClick={() => {
                        form.submit();
                    }}
                    onNoClick={() => {
                        closeDialog('downgradeWithFeaturesEnabled');
                        if (document.referrer.toLowerCase().indexOf('composer') > 0) window.location.href = '/Composer';
                        else navigate(backofficeUrl('/Dashboard'));
                    }}
                    message={messageLossFeatures}
                />
            );
        }

        // else if (props.subscription && props.subscription.isProfanityChecksEnabled && props.profileName == 'Starter') {
        //     showDialog('changeToPlanWithoutProfanityChecks', <MessageDialog
        //         message={T._('You can not change to STARTER plan cause you have profanity checks configured.', 'Admin')}
        //         buttons={[{ label: T._('OK', 'Admin'), onClick: () => { closeDialog('changeToPlanWithoutProfanityChecks') } }]}
        //     />);
        // }
        else if (
            props.subscription &&
            props.subscription.planId != props.planID &&
            !props.subscription.isOnMonthlyPlan &&
            !props.subscription.inTrial &&
            props.planPeriodSelected == 'MONTHLY'
        ) {
            const messageDowngrade = (
                <>
                    <p>
                        {T._('You are downgrading from an annual plan to a monthly plan.', 'Admin')}
                        <br />
                        <strong>{T._('No refund will be issued for the annual plan.', 'Admin')}</strong>
                    </p>
                    <p>
                        {T._("If you proceed you'll be paying the monthly fee for months already covered by your annual subscription.", 'Admin')}
                        <br />
                        <br />
                        {T._('Do you wish to continue?', 'Admin')}
                    </p>
                </>
            );

            showDialog(
                'onSubscriptionPlan',
                <QuestionDialog
                    message={messageDowngrade}
                    onYesClick={() => {
                        closeDialog('onSubscriptionPlan');
                        form.submit();
                    }}
                    onNoClick={() => {
                        closeDialog('onSubscriptionPlan');
                    }}
                />
            );
        } else if (
            props.planVersion == 'VERSION_1' &&
            props.ecommerce == 'SHOPIFY' &&
            props.profileName == 'Unlimited' &&
            props.subscription &&
            props.subscription.profileName &&
            props.subscription.profileName.indexOf('Config') > 0
        ) {
            const messageDowngradeFromConfigurator = (
                <>
                    {T._('Oops...it was not possible to downgrade your plan.', 'Admin')}
                    <br />
                    <br />
                    {T._('Please send us a message to', 'Admin')} <span>help@zakeke.com</span>
                </>
            );

            showDialog(
                'shopifyDowngradeFromConfiguratorPlan',
                <MessageDialog
                    message={messageDowngradeFromConfigurator}
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                closeDialog('shopifyDowngradeFromConfiguratorPlan');
                            }
                        }
                    ]}
                />
            );
        } else {
            form.submit();
        }
    };

    return (
        <FormPlanSubscribe className={props.className} method='POST' onSubmit={onSubmitForm} action={urlAction} $version={props.planVersion}>
            <input type='hidden' name='planID' value={props.planID} />
            {props.isMarketingAccepted != undefined && <input type='hidden' name='marketingAccepted' value={props.isMarketingAccepted ? 'true' : ''} />}

            {props.tooltip && (
                <TooltipAlignContentRight title={props.tooltip}>
                    <PrimaryButton
                        size={'small'}
                        data-track={'plan subscribed ' + props.planName}
                        type='submit'
                        className={props.profileName == 'Advanced' && !props.isPricingForCustomPlan ? 'advanced' : ''}
                        disabled={props.disabled ?? false}
                    >
                        {buttonText}
                    </PrimaryButton>
                </TooltipAlignContentRight>
            )}
            {/* il tooltip non si aggiorna se il title cambia, perciò è necessario il secondo bottone */}
            {!props.tooltip && (
                <PrimaryButton
                    size={'small'}
                    data-track={'plan subscribed ' + props.planName}
                    type='submit'
                    className={props.profileName == 'Advanced' && !props.isPricingForCustomPlan ? 'advanced' : ''}
                    disabled={props.disabled ?? false}
                >
                    {buttonText}
                </PrimaryButton>
            )}
        </FormPlanSubscribe>
    );
};

export const ButtonSubscribePlan = styled(_buttonSubscribePlan)``;
